// Adapts a Coach AI MorningBrief (BRIEFING investigation path) into the same
// InsightCard shape the Bento demo's insight feed renders, so the two surfaces
// share one visual and interaction language instead of a bespoke chat card.

import type {BriefItem, MorningBrief} from "./intelligenceModels";
import type {
    HeadlineMetric,
    InsightActionCard,
    InsightCard
} from "../../insightCardModels";
import {insightAgents, toAgentIdentity} from "../../insightAgents";
import type {AgentIdentity} from "../../insightAgents";

interface DomainMatch {
    agentId: string;
    keywords: string[];
}

// Keyword -> specialist mapping, reusing the same agent roster (and real avatar
// assets) the rest of the Bento insight feed already surfaces insights through.
const DOMAIN_MATCHES: DomainMatch[] = [
    {
        agentId: "agent_ops_pulse",
        keywords: ["sos", "speed", "drive-thru", "drive thru", "throughput", "wait"]
    },
    {
        agentId: "agent_pl_advisor",
        keywords: ["labor", "food cost", "cogs", "margin", "waste", "prime cost"]
    },
    {
        agentId: "agent_guest_pulse",
        keywords: ["guest", "csat", "satisfaction", "nps", "survey"]
    },
    {
        agentId: "agent_loss_intel",
        keywords: ["void", "discount", "shrink", "refund", "comp"]
    },
    {agentId: "agent_menu_core", keywords: ["menu", "mix"]},
    {
        agentId: "agent_loyalty_iq",
        keywords: ["loyalty", "enrollment", "redemption", "member"]
    }
];

/** Best-effort domain specialist for a metric name; undefined when nothing matches. */
export function pickAgentForMetric(metric: string): AgentIdentity | undefined {
    const lower = metric.toLowerCase();
    const match = DOMAIN_MATCHES.find(({keywords}) =>
        keywords.some((keyword) => lower.includes(keyword))
    );
    if (!match) {
        return undefined;
    }
    const agent = insightAgents.find((candidate) => candidate.id === match.agentId);
    return agent ? toAgentIdentity(agent) : undefined;
}

function slugify(text: string): string {
    return (
        text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "") || "item"
    );
}

function directionFromSeverity(
    severity: BriefItem["severity"]
): HeadlineMetric["direction"] {
    if (severity === "positive") return "positive";
    if (severity === "attention") return "negative";
    return "neutral";
}

function buildActions(item: BriefItem, scopeLabel: string): InsightActionCard[] {
    const idBase = `mb_${slugify(item.metric)}_${slugify(item.scope)}`;

    return [
        {
            id: `${idBase}_breakdown`,
            kind: "analysis",
            category: "View Breakdown",
            title: "View breakdown by segment",
            description: `See how ${item.metric.toLowerCase()} broke down at ${scopeLabel} during the window this was flagged.`,
            ctaLabel: "View Breakdown",
            workflow: {
                intro: `Pull a segment-level breakdown of ${item.metric} at ${scopeLabel}.`,
                bullets: [
                    {label: "Metric", value: item.metric},
                    {label: "Scope", value: scopeLabel},
                    {label: "Movement", value: item.movement}
                ],
                quickReplies: [`Break down ${item.metric} at ${scopeLabel} by daypart`]
            }
        },
        {
            id: `${idBase}_impact`,
            kind: "analysis",
            category: "Business Impact",
            title: "View business impact",
            description: `See what this movement means for sales, labor, or guest experience at ${scopeLabel}.`,
            ctaLabel: "View Impact",
            workflow: {
                intro: `Translate ${item.metric} at ${scopeLabel} into business impact.`,
                bullets: [
                    {label: "Current value", value: item.value},
                    {label: "Movement", value: item.movement}
                ],
                quickReplies: [`What did ${item.metric} at ${scopeLabel} cost us?`]
            }
        },
        {
            id: `${idBase}_staff`,
            kind: "ops_task",
            category: "Who Was Working",
            title: "Review who was working",
            description: `Check staffing and shift coverage at ${scopeLabel} during this window.`,
            ctaLabel: "Review Staff",
            workflow: {
                intro: `Pull the shift roster at ${scopeLabel} for the window ${item.metric} was flagged.`,
                bullets: [{label: "Scope", value: scopeLabel}],
                quickReplies: [`Who was working at ${scopeLabel} during this window?`]
            }
        },
        {
            id: `${idBase}_message`,
            kind: "comms",
            category: "Message Manager",
            title: "Message manager",
            description: `Send a heads-up to the manager on duty at ${scopeLabel} about this.`,
            ctaLabel: "Send Message",
            workflow: {
                intro: `Draft a message to the manager at ${scopeLabel} about ${item.metric}.`,
                bullets: [
                    {label: "Metric", value: item.metric},
                    {label: "Movement", value: item.movement}
                ],
                followUp: "Message drafted and ready to send.",
                quickReplies: [`Draft a message to the ${scopeLabel} manager about this`]
            }
        }
    ];
}

function buildInsightCardFromBriefItem(
    item: BriefItem,
    period: string,
    index: number
): InsightCard {
    const scopeLabel = item.scope || "this location";
    const agent = pickAgentForMetric(item.metric);

    return {
        id: `mb_${slugify(item.metric)}_${slugify(item.scope)}_${index}`,
        source: agent ? agent.specialty : `Morning Brief · ${period}`,
        headlineMetric: {
            value: item.value,
            label: `${item.metric} · ${scopeLabel}`,
            direction: directionFromSeverity(item.severity)
        },
        title: item.metric,
        tags: [scopeLabel, item.movement, item.value].filter(Boolean),
        narrative:
            `${item.metric} came in at ${item.value} for ${scopeLabel} (${item.movement}).` +
            (item.note ? ` ${item.note}` : ""),
        aiNote:
            item.severity === "attention"
                ? 'Flagged because it moved more than usual for this location. Ask "why" for a full root-cause breakdown.'
                : undefined,
        confidence: 89,
        actions: buildActions(item, scopeLabel)
    };
}

export interface MorningBriefHighlightCard {
    card: InsightCard;
    agent?: AgentIdentity;
}

/** One InsightCard per highlight, ready to render with the shared BentoInsightCard. */
export function buildInsightCardsFromMorningBrief(
    brief: MorningBrief
): MorningBriefHighlightCard[] {
    return (brief.highlights ?? []).map((item, index) => ({
        card: buildInsightCardFromBriefItem(item, brief.period, index),
        agent: pickAgentForMetric(item.metric)
    }));
}
